import { ConditionOperatorEnumType, type ConditionOperator } from '@reach/shared-types/conditions';
import { Check } from 'lucide-react';
import type { ComponentType } from 'react';
import { acceptsMultipleValues, supportsTokenList } from '../field-type-operators';
import styles from '../SegmentVisualBuilder.module.css';
import { BetweenDateInput } from './BetweenDateInput';
import { EqualsDateInput } from './EqualsDateInput';
import { GreaterLessThanDateInput } from './GreaterLessThanDateInput';
import { RelativeDateInput } from './RelativeDateInput';
import { SuggestedTextInput } from './SuggestedTextInput';
import { TokenizedValueInput } from './TokenizedValueInput';
import type { DateOperatorInputProps, ValueInputProps } from './types';

const VALUE_LESS_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.EXISTS,
  ConditionOperatorEnumType.NOT_EXISTS,
];

/** Operators that use free-text substring input; use text input even when enum is defined. */
const SUBSTRING_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.CONTAINS,
  ConditionOperatorEnumType.NOT_CONTAINS,
];

const DATE_OPERATOR_INPUTS: Partial<Record<ConditionOperator, ComponentType<DateOperatorInputProps>>> = {
  [ConditionOperatorEnumType.EQUALS_MONTH_DAY]: EqualsDateInput,
  [ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR]: EqualsDateInput,
  [ConditionOperatorEnumType.BETWEEN_MONTH_DAY]: BetweenDateInput,
  [ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR]: BetweenDateInput,
  [ConditionOperatorEnumType.GREATER_THAN]: GreaterLessThanDateInput,
  [ConditionOperatorEnumType.LESS_THAN]: GreaterLessThanDateInput,
};

function getDateOperatorInput(operator: ConditionOperator): ComponentType<DateOperatorInputProps> {
  return DATE_OPERATOR_INPUTS[operator] ?? RelativeDateInput;
}

function atLeastOneEnumStartsWith(enumOptions: string[], prefix: string): boolean {
  if (!prefix.trim()) return true;
  return enumOptions.some(opt => opt.startsWith(prefix));
}

/**
 * Polymorphic value input based on field type and operator.
 */
export function ValueInput({
  fieldType,
  operator,
  value,
  onChange,
  disabled = false,
  enumOptions,
  schemaId,
  field,
}: ValueInputProps) {
  if (VALUE_LESS_OPERATORS.includes(operator)) {
    return <span className={styles.conditionRowValue} aria-hidden />;
  }

  const updateSingleTextValue = (nextValue: string) => onChange([nextValue]);

  const hasMultipleStoredValues = supportsTokenList(fieldType) && value.length > 1;
  const needsMultiValueInput = acceptsMultipleValues(operator, fieldType) || hasMultipleStoredValues;
  const bypassesEnumDropdown = !enumOptions?.length || SUBSTRING_OPERATORS.includes(operator);
  const requiresTokenizedInput = needsMultiValueInput && bypassesEnumDropdown;
  if (requiresTokenizedInput) {
    return (
      <TokenizedValueInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        numeric={fieldType === 'number'}
        schemaId={schemaId}
        field={field}
      />
    );
  }

  if (fieldType === 'string' || (fieldType === 'unknown' && enumOptions?.length)) {
    const useEnumDropdown =
      enumOptions && enumOptions.length > 0 && !SUBSTRING_OPERATORS.includes(operator);
    if (useEnumDropdown) {
      const visibleOptions = Array.from(
        new Set([...enumOptions, ...value.filter((item): item is string => typeof item === 'string')]),
      );
      if (acceptsMultipleValues(operator, fieldType) || value.length > 1) {
        const selected = Array.isArray(value) ? (value as string[]) : [];
        const toggleOption = (opt: string) => {
          if (disabled) return;
          const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
          onChange(next);
        };
        return (
          <div
            className={styles.enumCheckboxList}
            role="listbox"
            aria-label="Values (select multiple)"
            aria-multiselectable>
            {visibleOptions.map(opt => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={styles.enumCheckboxOption}
                  onClick={() => toggleOption(opt)}
                  disabled={disabled}>
                  <span
                    className={isSelected ? styles.enumCheckboxIconChecked : styles.enumCheckboxIcon}
                    aria-hidden>
                    {isSelected ? <Check size={14} strokeWidth={2.5} /> : null}
                  </span>
                  <span className={styles.enumCheckboxLabel}>{opt}</span>
                </button>
              );
            })}
          </div>
        );
      }
      const strVal = (value?.[0] as string | undefined) ?? '';
      return (
        <select
          className={styles.selectInput}
          value={strVal}
          onChange={e => onChange([e.target.value])}
          disabled={disabled}
          aria-label="Value">
          <option value="">Select...</option>
          {visibleOptions.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    if (fieldType === 'string') {
      if (SUBSTRING_OPERATORS.includes(operator) && enumOptions && enumOptions.length > 0) {
        const strVal = (value?.[0] as string | undefined) ?? '';
        const invalidPrefix = strVal.trim() !== '' && !atLeastOneEnumStartsWith(enumOptions, strVal);
        return (
          <div className={styles.conditionRowValue}>
            <input
              type="text"
              className={invalidPrefix ? `${styles.textInput} ${styles.inputInvalid}` : styles.textInput}
              value={strVal}
              onChange={e => onChange([e.target.value])}
              disabled={disabled}
              aria-label="Value"
              aria-invalid={invalidPrefix}
            />
            {invalidPrefix && (
              <span className={styles.validationMessage} role="alert">
                No enum value starts with this prefix
              </span>
            )}
          </div>
        );
      }
      const strVal = (value?.[0] as string | undefined) ?? '';
      return (
        <SuggestedTextInput
          value={strVal}
          onChange={updateSingleTextValue}
          disabled={disabled}
          schemaId={schemaId}
          field={field}
        />
      );
    }
  }

  if (fieldType === 'number') {
    const numVal = value?.[0];
    const str = typeof numVal === 'number' ? String(numVal) : numVal != null ? String(numVal) : '';
    return (
      <input
        type="number"
        className={styles.textInput}
        value={str}
        onChange={e => {
          const nextValue = e.target.value;
          if (nextValue === '') {
            onChange([]);
            return;
          }
          const parsed = Number(nextValue);
          if (!Number.isNaN(parsed)) onChange([parsed]);
        }}
        disabled={disabled}
        aria-label="Value"
      />
    );
  }

  if (fieldType === 'boolean') {
    const boolVal = value?.[0];
    const selected = boolVal === true ? 'true' : boolVal === false ? 'false' : '';
    return (
      <select
        className={styles.selectInput}
        value={selected}
        onChange={e => {
          const nextValue = e.target.value;
          if (nextValue === 'true') onChange([true]);
          else if (nextValue === 'false') onChange([false]);
          else onChange([]);
        }}
        disabled={disabled}
        aria-label="Value">
        <option value="">Select...</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (fieldType === 'date') {
    const DateOperatorInput = getDateOperatorInput(operator);
    return (
      <DateOperatorInput
        operator={operator}
        value={value?.[0]}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (fieldType === 'array' && operator === 'array_contains') {
    const strVal = (value?.[0] as string | undefined) ?? '';
    return (
      <SuggestedTextInput
        value={strVal}
        onChange={updateSingleTextValue}
        disabled={disabled}
        schemaId={schemaId}
        field={field}
      />
    );
  }

  // unknown or fallback: text input
  const strVal = (value?.[0] as string | undefined) ?? '';
  return (
    <input
      type="text"
      className={styles.textInput}
      value={strVal}
      onChange={e => onChange([e.target.value])}
      disabled={disabled}
      aria-label="Value"
    />
  );
}
